import clsx from 'clsx';
import { useCallback, useEffect, useState } from 'react';
import { AutoCompleteInput } from 'src/components/AutoCompleteInput';
import {
  AutoCompleteSelectBox,
  Option,
} from 'src/components/AutoCompleteSelectBox';
import { Input } from 'src/components/Input';
import { Label } from 'src/components/Label';
import { Loading } from 'src/components/Loading';
import { SchemaBuilder } from 'src/components/SchemaBuilder';
import { SelectBox } from 'src/components/SelectBox';
import { Textarea } from 'src/components/Textarea';
import { Toggle } from 'src/components/Toggle';
import { AccountConnection } from 'src/interfaces/accountConnection.interface';
import {
  ActionPayload,
  ActionType,
  PhraseTriggerConditionType,
  ProcessingMode,
  TriggerType,
} from 'src/interfaces/action.interface';
import { providerOptions } from 'src/pages/AccountConnections/providerOptions';
import { Agent } from 'src/interfaces/agent.interface';
import { triggerTypes } from 'src/stubs/action.stub';
import { getFirstAndLastFourUUID } from 'src/utils/number';
import { providerDisplayNames } from 'src/utils/provider';
import { isValidHttpUrl } from 'src/utils/url';
import useAuthedRequest from 'src/hooks/useAuthedRequest';
import { validateActionPhoneNumber } from 'src/utils/telephony';
import { ActionFormWarmTransfer } from './ActionFormWarmTransfer';

interface ActionFormContentProps {
  form: ActionPayload;
  setForm: (form: ActionPayload) => void;
  loading: boolean;
  showRequiredFields?: boolean;
  phoneNumberError?: string | null;
  setPhoneNumberError?: (err: string | null) => void;
}

export const ActionFormContent = ({
  form,
  setForm,
  loading,
  showRequiredFields,
  phoneNumberError: externalPhoneNumberError,
  setPhoneNumberError: setExternalPhoneNumberError,
}: ActionFormContentProps) => {
  const { get } = useAuthedRequest();
  const [inputsWithError, setInputsWithError] = useState<string[]>([]);
  const [attachedAccountConnectionOption, setAttachedAccountConnectionOption] =
    useState<Option>();
  const [phoneNumberError, setPhoneNumberError] = useState<string | null>(null);

  const fetchAccountConnections = useCallback(async () => {
    const data = await get('/account-connections');

    return data.items
      .filter((ac: AccountConnection) =>
        providerOptions.find(
          (providerOption) =>
            providerOption.value === ac.type && providerOption.type === 'Auth',
        ),
      )
      .map((ac: AccountConnection) => ({
        value: ac.id,
        label: `${providerDisplayNames[ac.type]} - ${getFirstAndLastFourUUID(ac.id || '')}`,
      }));
  }, []);

  const fetchAccountConnectionById = useCallback(async (id: string) => {
    const ac = await get(`/account-connections/${id}`);

    setAttachedAccountConnectionOption({
      value: ac.id,
      label: `${providerDisplayNames[ac.type]} - ${getFirstAndLastFourUUID(ac.id || '')}`,
    });
  }, []);

  const fetchLastNumbers = async (search: string) => {
    try {
      const data = await get(`/numbers/recent?number=${search}`);
      return data.map((i: { id: string; number: string }) => ({
        id: i.id,
        label: i.number,
        value: i.number,
      }));
    } catch (error) {
      console.error('Failed to fetch last numbers:', error);
      return [];
    }
  };

  const fetchAgents = useCallback(async (search: string) => {
    try {
      const query = new URLSearchParams({
        filters: JSON.stringify({ name: search ? search : undefined }),
      });

      const data = await get(`/agents?${query.toString()}`);

      return data.items.map((agent: Agent) => ({
        value: agent.id,
        label: agent.name,
      }));
    } catch (error) {
      console.error('Failed to fetch agents:', error);
      return [];
    }
  }, []);

  const fetchAgentById = useCallback(async (id: string) => {
    try {
      return get(`/agents/${id}`);
    } catch (error) {
      console.error('Failed to fetch agents:', error);
      return [];
    }
  }, []);

  const handleUrlChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (!isValidHttpUrl(e.target.value)) {
      setInputsWithError([...inputsWithError, 'url']);
      setForm({
        ...form,
        config: {
          ...form.config,
          url: '',
        },
      });
    } else {
      setInputsWithError(inputsWithError.filter((input) => input !== 'url'));
    }
  };

  useEffect(() => {
    if (!form.config?.account_connection_id) {
      setAttachedAccountConnectionOption(undefined);
      return;
    }
    fetchAccountConnectionById(form.config.account_connection_id);
  }, [form?.config?.account_connection_id]);

  const needsPhoneNumber =
    form.type === ActionType.TransferCall ||
    form.type === ActionType.AddToConference ||
    form.type === ActionType.WarmTransfer;

  const canChangeTrigger = form.type !== ActionType.External;

  // Use external error state if provided
  const error =
    externalPhoneNumberError !== undefined
      ? externalPhoneNumberError
      : phoneNumberError;
  const setError = setExternalPhoneNumberError || setPhoneNumberError;

  if (form.type === ActionType.WarmTransfer) {
    return (
      <ActionFormWarmTransfer
        form={form}
        setForm={setForm}
        loading={loading}
        showRequiredFields={showRequiredFields}
        phoneNumberError={phoneNumberError}
        setPhoneNumberError={setPhoneNumberError}
      />
    );
  }

  return (
    <div className="p-2">
      {showRequiredFields && (
        <div className="flex justify-end italic text-sm mt-4">
          * Required fields
        </div>
      )}
      <div>
        {form.type === ActionType.External && (
          <div className="mb-6">
            <Input
              label="What is the action name?"
              type="text"
              id="name"
              placeholder="Enter Action Name"
              value={form.config.name}
              onChange={(e) =>
                setForm({
                  ...form,
                  config: {
                    ...form.config,
                    name: e.target.value as string,
                  },
                })
              }
              disabled={loading}
              required
            />
          </div>
        )}

        {form.type !== ActionType.External && (
          <div className="mb-6 w-full md:w-[60%] lg:w-[35%] min-w-[300px]">
            <Input
              label="What is the action name?"
              type="text"
              id="label"
              placeholder="Enter Action Name"
              value={form.label}
              onChange={(e) =>
                setForm({
                  ...form,
                  label: e.target.value as string,
                })
              }
              disabled={loading}
            />
          </div>
        )}

        {form.type === ActionType.External && (
          <div className="mb-6">
            <Input
              label="How should this action be triggered?"
              type="text"
              id="description"
              placeholder="Ex: 'If the user asks to create an appointment, then this action would be triggered'"
              value={form.config.description}
              onChange={(e) =>
                setForm({
                  ...form,
                  config: {
                    ...form.config,
                    description: e.target.value as string,
                  },
                })
              }
              disabled={loading}
              required
            />
          </div>
        )}

        {form.type !== ActionType.External && (
          <div className="mb-6 w-full md:w-[60%] lg:w-[35%] min-w-[300px]">
            <Label htmlFor="description">
              How can we describe this action?
            </Label>
            <Textarea
              id="description"
              placeholder="Ex: this action is used to create an appointment"
              value={form.description}
              onChange={(e) =>
                setForm({
                  ...form,
                  description: e.target.value,
                })
              }
              disabled={loading}
              rows={4}
            />
          </div>
        )}

        {form.type === ActionType.External && (
          <>
            <div className="mb-6">
              <Input
                label="What is the API URL?"
                type="text"
                id="url"
                placeholder="Ex: https://www.google.com/maps/search/?api=1&parameters"
                className={clsx(
                  'w-full px-3 py-2 border border-neutral-200 rounded-md',
                  inputsWithError.includes('url') && 'border-error',
                )}
                value={form.config.url}
                onChange={(e) =>
                  setForm({
                    ...form,
                    config: {
                      ...form.config,
                      url: e.target.value as string,
                    },
                  })
                }
                onBlur={handleUrlChange}
                disabled={loading}
                required
              />

              {inputsWithError.includes('url') && (
                <span className="text-error text-xs font-bold">
                  Please enter a valid URL
                </span>
              )}
            </div>

            <div className="mb-6">
              <Label htmlFor="account-connection">Account Connection</Label>

              <AutoCompleteSelectBox
                key={attachedAccountConnectionOption?.value}
                defaultValue={attachedAccountConnectionOption}
                fetchOptions={fetchAccountConnections}
                onChange={(value) =>
                  setForm({
                    ...form,
                    config: {
                      ...form.config,
                      account_connection_id: value,
                    },
                  })
                }
                variant="outlined"
                color="primary"
                size="medium"
                disabled={loading}
              />
            </div>

            <div className="mb-6">
              <Input
                label="Signature Secret"
                type="text"
                id="signature_secret"
                placeholder="Ex: 1234567890"
                value={form.config.signature_secret}
                onChange={(e) =>
                  setForm({
                    ...form,
                    config: {
                      ...form.config,
                      signature_secret: e.target.value as string,
                    },
                  })
                }
                disabled={loading}
              />
            </div>

            <div>
              <div className="mb-6 flex items-center justify-between">
                <div>
                  <Label required htmlFor="speak_on_send" className="mb-0">
                    Speak on Send
                  </Label>
                  <span className="text-sm text-neutral-500">
                    Toggle On to allow the Voice AI Agent to respond to the
                    caller before an external action starts.
                  </span>
                </div>
                <Toggle
                  initialState={form.config.speak_on_send}
                  onChange={(value) =>
                    setForm({
                      ...form,
                      config: { ...form.config, speak_on_send: value },
                    })
                  }
                  disabled={loading}
                />
              </div>

              <div className="mb-6 flex items-center justify-between">
                <div>
                  <Label required htmlFor="speak_on_receive">
                    Speak on Receive
                  </Label>
                  <span className="text-sm text-neutral-500">
                    Toggle On to allow the Voice AI Agent responds immediately
                    when it receives data or a result from an external action
                  </span>
                </div>
                <Toggle
                  initialState={form.config.speak_on_receive}
                  onChange={(value) =>
                    setForm({
                      ...form,
                      config: { ...form.config, speak_on_receive: value },
                    })
                  }
                  disabled={loading}
                />
              </div>
            </div>

            <div className="mb-6">
              <Label htmlFor="processing_mode">Processing Mode</Label>
              <SelectBox
                key={form.config.processing_mode}
                options={[{ label: 'Muted', value: 'muted' }]}
                defaultValue={{ label: 'Muted', value: 'muted' }}
                variant="outlined"
                color="primary"
                size="medium"
                onChange={(value) =>
                  setForm({
                    ...form,
                    config: {
                      ...form.config,
                      processing_mode: value as ProcessingMode,
                    },
                  })
                }
                className="min-w-[140px] bg-neutral-0"
                disabled={loading}
              />
            </div>

            <div className="mb-6">
              <Label htmlFor="input_schema">Input Schema</Label>
              <SchemaBuilder
                key={form.config.name || ''}
                value={form.config.input_schema || ''}
                onChange={(schema) =>
                  setForm({
                    ...form,
                    config: {
                      ...form.config,
                      input_schema: schema,
                    },
                  })
                }
              />
            </div>
          </>
        )}

        {needsPhoneNumber && (
          <div className="mb-6 w-full md:w-[60%] lg:w-[35%] min-w-[300px]">
            <AutoCompleteInput
              label={
                form.type === ActionType.TransferCall
                  ? 'What is the transfer number?'
                  : 'What is the number to dial?'
              }
              id="phone-number"
              placeholder={
                form.type === ActionType.TransferCall
                  ? 'Enter Transfer Number'
                  : 'Enter Number to Dial'
              }
              value={form.config.phone_number || ''}
              onChange={(value) => {
                const { error } = validateActionPhoneNumber(form.type, value);
                setError(error);
                setForm({
                  ...form,
                  config: {
                    ...form.config,
                    phone_number: value as string,
                  },
                });
              }}
              fetchOptions={fetchLastNumbers}
              disabled={loading}
              required
            />
            {error && <div className="text-error text-xs mt-1">{error}</div>}
          </div>
        )}
      </div>

      {canChangeTrigger && (
        <div className="mb-6">
          <Label required htmlFor="action-trigger">
            What should trigger this action?
          </Label>

          <div className="flex flex-wrap gap-4">
            {triggerTypes.map((trigger) => (
              <div
                key={trigger.value}
                className={clsx(
                  'border rounded-lg cursor-pointer w-full md:w-[60%] lg:w-[35%] min-w-[300px]',
                  'hover:border-primary/25 hover:shadow-md',
                  form.action_trigger.type === trigger.value
                    ? 'p-1 action-box-selected rounded-lg shadow-md'
                    : 'p-3 m-1 border-neutral-100 bg-neutral-0',
                  form.type === ActionType.DTMF &&
                    trigger.value === TriggerType.PhraseBased &&
                    'opacity-50 cursor-not-allowed',
                )}
                onClick={() => {
                  if (
                    form.type === ActionType.DTMF &&
                    trigger.value === TriggerType.PhraseBased
                  ) {
                    return;
                  }

                  setForm({
                    ...form,
                    action_trigger: { type: trigger.value },
                  });
                }}
              >
                <div
                  className={clsx(
                    'bg-neutral-0',
                    form.action_trigger.type === trigger.value &&
                      'p-3 rounded-md h-full',
                  )}
                >
                  <div className="flex items-start mb-2 justify-between">
                    <p className="font-regular">{trigger.label}</p>
                    <div
                      className={clsx(
                        'w-[18px] h-[18px] rounded-full border b-1 border-neutral-200',
                        form.action_trigger.type === trigger.value
                          ? 'bg-primary-300'
                          : 'bg-neutral-0',
                      )}
                    ></div>
                  </div>
                  <p className="text-sm text-neutral-500">
                    {trigger.description}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {form.action_trigger.type === TriggerType.PhraseBased && (
        <div className="mb-6 w-full md:w-[60%] lg:w-[35%] min-w-[300px]">
          <Input
            label="What is the phrase to trigger on?"
            type="text"
            id="phrase-trigger"
            placeholder="Ex: 'Transfer me'"
            value={
              (
                form.action_trigger.config as {
                  phrase_triggers?: { phrase: string }[];
                }
              )?.phrase_triggers?.[0]?.phrase || ''
            }
            onChange={(e) =>
              setForm({
                ...form,
                action_trigger: {
                  ...form.action_trigger,
                  config: {
                    phrase_triggers: [
                      {
                        phrase: e.target.value as string,
                        conditions: [
                          PhraseTriggerConditionType.PhraseConditionTypeContains,
                        ],
                      },
                    ],
                  },
                },
              })
            }
            disabled={loading}
            required
          />
        </div>
      )}

      {form.type === ActionType.AddToConference && (
        <div className="mb-6 flex items-center justify-between w-full">
          <div>
            <Label required htmlFor="place_primary_on_hold" className="mb-0">
              Place Primary on Hold
            </Label>
            <span className="text-sm text-neutral-500">
              Toggle On to allow the Voice AI Agent to place the primary on
              hold.
            </span>
          </div>
          <Toggle
            initialState={form.config.place_primary_on_hold}
            onChange={(value) =>
              setForm({
                ...form,
                config: {
                  ...form.config,
                  place_primary_on_hold: value,
                },
              })
            }
            disabled={loading}
          />
        </div>
      )}
    </div>
  );
};
